import type { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { EmailEmailReadModel } from '@r/api/generated/client.schemas.ts';
import { DataTableColumnHeader } from '@r/components/data-table/data-table-column-header.tsx';
import type { DataTableFilterField } from '@r/types/index.ts';
import { cn } from '@r/utils/utils.ts';
import { Badge } from '@ui/badge.tsx';

/** Statuts connus renvoyés par l'API (champ `status` typé `string` côté back). */
const EMAIL_STATUSES = ['sent', 'opened', 'clicked', 'error'] as const;

const EMAIL_STATUS_DOT_CLASS: Record<string, string> = {
  sent: 'bg-emerald-600',
  opened: 'bg-blue-500',
  clicked: 'bg-violet-500',
  error: 'bg-destructive'
};

/** Badge du dernier statut connu ; une valeur inconnue est affichée brute en gris. */
function EmailStatusBadge({ status }: { status: string | null | undefined }) {
  const { t } = useTranslation();
  const dotClass = status ? EMAIL_STATUS_DOT_CLASS[status] : undefined;

  return (
    <Badge variant="outline" className="gap-1.5 border-muted-foreground/20 font-normal">
      <span
        className={cn('size-1.5 shrink-0 rounded-full', dotClass ?? 'bg-muted-foreground')}
        aria-hidden
      />
      {dotClass ? t(`admin.emails.status.${status}`) : status || t('admin.emails.status.unknown')}
    </Badge>
  );
}

function formatDate(value: string | null | undefined) {
  return value ? format(new Date(value), 'dd/MM/yyyy') : '';
}

/**
 * Champs de filtrage de l'historique des e-mails : recherche texte sur l'objet et le
 * destinataire, facettes sur le statut, plages de dates sur l'envoi et la mise à jour.
 */
export function useEmailHistoryFilterFields(): DataTableFilterField<EmailEmailReadModel>[] {
  const { t } = useTranslation();

  // Mémorisé : un nouveau tableau à chaque render ferait reconstruire le modèle de la table.
  return useMemo(
    () => [
      {
        label: t('admin.emails.column.subject'),
        value: 'subject',
        placeholder: t('admin.emails.column.subject')
      },
      {
        label: t('admin.emails.column.to'),
        value: 'to',
        placeholder: t('admin.emails.column.to')
      },
      {
        label: t('admin.emails.column.sent.at'),
        value: 'sentAt',
        placeholder: t('admin.emails.column.sent.at'),
        isDateRange: true
      },
      {
        label: t('admin.emails.column.status'),
        value: 'status',
        options: EMAIL_STATUSES.map((status) => ({
          label: t(`admin.emails.status.${status}`),
          value: status
        }))
      },
      {
        label: t('admin.emails.column.status.updated.at'),
        value: 'statusUpdatedAt',
        placeholder: t('admin.emails.column.status.updated.at'),
        isDateRange: true
      }
    ],
    [t]
  );
}

/**
 * Colonnes de l'historique des e-mails (fiches société et utilisateur). Seuls date d'envoi,
 * statut et date de mise à jour sont triables (tri supporté par l'API).
 */
export function useEmailHistoryColumns(): ColumnDef<EmailEmailReadModel>[] {
  const { t } = useTranslation();

  // Mémorisé : un nouveau tableau à chaque render ferait reconstruire le modèle de la table.
  return useMemo(
    () => [
      {
        accessorKey: 'subject',
        enableHiding: false,
        enableSorting: false,
        header: t('admin.emails.column.subject'),
        cell: ({ row }) => <span className="font-medium">{row.original.subject}</span>
      },
      {
        accessorKey: 'to',
        enableHiding: false,
        enableSorting: false,
        header: t('admin.emails.column.to')
      },
      {
        accessorKey: 'sentAt',
        enableHiding: false,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('admin.emails.column.sent.at')} />
        ),
        cell: ({ row }) => formatDate(row.original.sentAt)
      },
      {
        accessorKey: 'status',
        enableHiding: false,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('admin.emails.column.status')} />
        ),
        cell: ({ row }) => <EmailStatusBadge status={row.original.status} />
      },
      {
        accessorKey: 'statusUpdatedAt',
        enableHiding: false,
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title={t('admin.emails.column.status.updated.at')}
          />
        ),
        cell: ({ row }) => formatDate(row.original.statusUpdatedAt)
      }
    ],
    [t]
  );
}
